import { makeAutoObservable } from 'mobx'

import type {
  KyrgyzstanOrderData,
  OrdersListRequest,
} from 'shared/api/kyrgyzstan/orders'
import { KyrgyzstanOrdersApi } from 'shared/api/kyrgyzstan/orders'
import { getApiErrorMessage } from 'shared/lib/errorText'

class KyrgyzstanOrdersStore {
  lastActiveOrder: KyrgyzstanOrderData | null = null
  ordersList: KyrgyzstanOrderData[] = []
  orderDetail: KyrgyzstanOrderData | null = null
  currentDetailOrderId: string | null = null

  isLoadingLastActiveOrder = false
  isLoadingList = false
  isLoadingDetail = false
  isLoadingDecline = false

  errorLastActiveOrder: string | null = null
  errorList: string | null = null
  errorDetail: string | null = null
  errorDecline: string | null = null

  page = 1
  pageSize = 20
  totalPages = 1
  totalItems = 0
  hasMore = false

  abortController: AbortController | null = null

  constructor() {
    makeAutoObservable(this)
  }

  setLastActiveOrder = (order: KyrgyzstanOrderData | null) => {
    this.lastActiveOrder = order
  }

  setLoadingLastActiveOrder = (loading: boolean) => {
    this.isLoadingLastActiveOrder = loading
  }

  setErrorLastActiveOrder = (error: string | null) => {
    this.errorLastActiveOrder = error
  }

  setOrdersList = (orders: KyrgyzstanOrderData[]) => {
    this.ordersList = orders
  }

  appendOrdersList = (orders: KyrgyzstanOrderData[]) => {
    this.ordersList = [...this.ordersList, ...orders]
  }

  setLoadingList = (loading: boolean) => {
    this.isLoadingList = loading
  }

  setErrorList = (error: string | null) => {
    this.errorList = error
  }

  setPage = (page: number) => {
    this.page = page
  }

  setTotalPages = (totalPages: number) => {
    this.totalPages = totalPages
  }

  setTotalItems = (totalItems: number) => {
    this.totalItems = totalItems
  }

  setHasMore = (hasMore: boolean) => {
    this.hasMore = hasMore
  }

  setOrderDetail = (order: KyrgyzstanOrderData | null) => {
    this.orderDetail = order
  }

  setLoadingDetail = (loading: boolean) => {
    this.isLoadingDetail = loading
  }

  setErrorDetail = (error: string | null) => {
    this.errorDetail = error
  }

  fetchLastActiveOrder = async () => {
    if (this.lastActiveOrder) {
      return
    }

    this.setLoadingLastActiveOrder(true)
    this.setErrorLastActiveOrder(null)

    try {
      const { data } = await KyrgyzstanOrdersApi.getLastActiveOrder()
      this.setLastActiveOrder(data.data)
    } catch (error) {
      const errorMessage = getApiErrorMessage(error)
      this.setErrorLastActiveOrder(errorMessage)
      this.setLastActiveOrder(null)
    } finally {
      this.setLoadingLastActiveOrder(false)
    }
  }

  fetchOrdersList = async (params: OrdersListRequest) => {
    const isNewSearch = params.page === 1

    if (isNewSearch && this.abortController) {
      this.abortController.abort()
    }

    this.setLoadingList(true)
    this.setErrorList(null)

    const abortController = new AbortController()

    if (isNewSearch) {
      this.abortController = abortController
    }

    try {
      const { data } = await KyrgyzstanOrdersApi.getOrdersList(
        {
          ...params,
          page_size: params.page_size || this.pageSize,
        },
        abortController.signal,
      )

      if (isNewSearch) {
        this.setOrdersList(data.data)
      } else {
        this.appendOrdersList(data.data)
      }

      this.setPage(data.page)
      this.setTotalPages(data.total_pages || 1)
      this.setTotalItems(data.total_items || 0)
      this.setHasMore(data.page < (data.total_pages || 1))
    } catch (error) {
      const errorMessage = getApiErrorMessage(error)

      if (errorMessage === null) {
        return
      }

      this.setErrorList(errorMessage)
      if (isNewSearch) {
        this.setOrdersList([])
      }
    } finally {
      this.setLoadingList(false)
    }
  }

  loadMoreOrders = async (activeOnly: boolean) => {
    if (this.isLoadingList || !this.hasMore) {
      return
    }

    await this.fetchOrdersList({
      active_only: activeOnly,
      page: this.page + 1,
      page_size: this.pageSize,
    })
  }

  clearLastActiveOrder = () => {
    this.lastActiveOrder = null
    this.errorLastActiveOrder = null
  }

  fetchOrderDetail = async (orderId: string) => {
    if (this.currentDetailOrderId === orderId && this.orderDetail) {
      return
    }

    this.setCurrentDetailOrderId(orderId)

    this.setLoadingDetail(true)
    this.setErrorDetail(null)

    try {
      const { data } = await KyrgyzstanOrdersApi.getOrderDetail(orderId)
      this.setOrderDetail(data.data)
    } catch (error) {
      const errorMessage = getApiErrorMessage(error)
      this.setErrorDetail(errorMessage)
      this.setOrderDetail(null)
    } finally {
      this.setLoadingDetail(false)
    }
  }

  clearOrderDetail = () => {
    this.orderDetail = null
    this.errorDetail = null
    this.currentDetailOrderId = null
  }

  setCurrentDetailOrderId = (orderId: string | null) => {
    this.currentDetailOrderId = orderId
  }

  clearDeclineError = () => {
    this.errorDecline = null
  }

  setLoadingDecline = (loading: boolean) => {
    this.isLoadingDecline = loading
  }

  setErrorDecline = (error: string | null) => {
    this.errorDecline = error
  }

  declineOrder = async (orderId: string) => {
    this.setLoadingDecline(true)
    this.setErrorDecline(null)

    try {
      await KyrgyzstanOrdersApi.declineOrder(orderId)
    } catch (error) {
      const errorMessage = getApiErrorMessage(error)
      this.setErrorDecline(errorMessage)
      throw error
    } finally {
      this.setLoadingDecline(false)
    }
  }

  clearOrdersList = () => {
    if (this.abortController) {
      this.abortController.abort()
      this.abortController = null
    }

    this.ordersList = []
    this.page = 1
    this.totalPages = 1
    this.totalItems = 0
    this.hasMore = false
    this.errorList = null
  }
}

export const kyrgyzstanOrdersStore = new KyrgyzstanOrdersStore()
